import React, { createContext, useContext, useEffect, useState, useCallback, ReactNode, useMemo } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { getOAuthCallbackUrl } from '@/lib/oauth';
import { hasSuperAdminAccess } from '@/lib/auth/superAdmin';
import { setSentryUser } from '@/lib/sentry';
import {
  ClientDashboardMode,
  persistClientDashboardMode,
  resolveClientDashboardMode,
} from '@/lib/clientDashboardMode';

export type UserRole = 'lawyer' | 'agent' | 'client';

export interface Profile {
  id: string;
  role: UserRole;
  created_at?: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  avatar_url: string | null;
  organization_name: string | null;
  license_number: string | null;
  is_verified: boolean;
  nin_verified: boolean;
  google_role_pending?: boolean | null;
  date_of_birth?: string | null;
  gender?: 'male' | 'female' | null;
  occupation?: string | null;
  nin?: string | null;
  professional_bio?: string | null;
  years_experience?: number | null;
  specialties?: string[] | null;
  service_states?: string[] | null;
  kyc_status?: 'incomplete' | 'pending_review' | 'approved' | 'rejected' | null;
  kyc_submitted_at?: string | null;
  kyc_reviewed_at?: string | null;
  kyc_reviewed_by?: string | null;
  kyc_rejection_reason?: string | null;
  account_status?: 'active' | 'suspended' | 'blocked' | null;
  account_status_reason?: string | null;
  suspended_until?: string | null;
  notification_preferences?: Record<string, boolean> | null;
  browser_push_enabled?: boolean | null;
  is_super_admin?: boolean | null;
  state_of_residence: string | null;
  address: string | null;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  clientDashboardMode: ClientDashboardMode;
  isLoading: boolean;
  isSuperAdmin: boolean;
  refreshProfile: () => Promise<void>;
  updateClientDashboardMode: (mode: ClientDashboardMode) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (data: SignUpData) => Promise<SignUpResult>;
  signOut: () => Promise<void>;
}

export interface SignUpData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone: string;
  role: UserRole;
  organizationName?: string;
  licenseNumber?: string;
}

export interface SignUpResult {
  requiresEmailConfirmation: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
const requiredConsentTypes = ['terms-of-service', 'privacy-policy'] as const;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [clientDashboardMode, setClientDashboardMode] = useState<ClientDashboardMode>('buyer');
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  const fetchProfile = useCallback(async (userId: string) => {
    await supabase.rpc('resolve_account_access');

    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (error) {
      console.error('Error fetching profile:', error);
      return null;
    }
    setSentryUser({
      id: data.id,
      email: data.email,
      username: `${data.first_name || ''} ${data.last_name || ''}`.trim() || data.email,
    });
    return data as Profile;
  }, []);

  const syncConsentRecords = useCallback(async (currentUser: User) => {
    const termsAccepted = currentUser.user_metadata?.terms_accepted === true;
    if (!termsAccepted) {
      return;
    }

    const { data: existingConsents, error: consentError } = await supabase
      .from('consent_records')
      .select('consent_type')
      .eq('user_id', currentUser.id);

    if (consentError) {
      console.error('Error fetching consent records:', consentError);
      return;
    }

    const existingTypes = new Set((existingConsents || []).map((consent) => consent.consent_type));
    const missingConsents = requiredConsentTypes
      .filter((consentType) => !existingTypes.has(consentType))
      .map((consentType) => ({
        user_id: currentUser.id,
        consent_type: consentType,
        consented: true,
        user_agent: navigator.userAgent,
      }));

    if (missingConsents.length === 0) {
      return;
    }

    const { error: insertError } = await supabase
      .from('consent_records')
      .insert(missingConsents as never);

    if (insertError) {
      console.error('Error inserting consent records:', insertError);
    }
  }, []);

  const refreshProfile = useCallback(async () => {
    if (!user) {
      setProfile(null);
      return;
    }

    const latestProfile = await fetchProfile(user.id);
    setProfile(latestProfile);
  }, [fetchProfile, user]);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, currentSession) => {
        setSession(currentSession);
        setUser(currentSession?.user ?? null);
        setClientDashboardMode(
          resolveClientDashboardMode(
            currentSession?.user?.user_metadata?.client_dashboard_mode,
            currentSession?.user?.id,
          ),
        );

        if (currentSession?.user) {
          // Use setTimeout to avoid Supabase client deadlock
          setTimeout(async () => {
            const prof = await fetchProfile(currentSession.user.id);
            await syncConsentRecords(currentSession.user);
            setProfile(prof);
            setIsLoading(false);
          }, 0);
        } else {
          setProfile(null);
          setSentryUser(null);
          setIsLoading(false);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session: existingSession } }) => {
      setSession(existingSession);
      setUser(existingSession?.user ?? null);
      setClientDashboardMode(
        resolveClientDashboardMode(
          existingSession?.user?.user_metadata?.client_dashboard_mode,
          existingSession?.user?.id,
        ),
      );
      if (existingSession?.user) {
        Promise.all([
          fetchProfile(existingSession.user.id),
          syncConsentRecords(existingSession.user),
        ]).then(([prof]) => {
          setProfile(prof);
          setIsLoading(false);
        });
      } else {
        setSentryUser(null);
        setIsLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, [fetchProfile, syncConsentRecords]);

  const updateClientDashboardMode = async (mode: ClientDashboardMode) => {
    if (!user) {
      throw new Error('Not authenticated');
    }

    setClientDashboardMode(mode);
    persistClientDashboardMode(mode, user.id);

    const { data, error } = await supabase.auth.updateUser({
      data: {
        client_dashboard_mode: mode,
      },
    });

    if (error) {
      return;
    }

    if (data.user) {
      setUser(data.user);
    }
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  };

  const signUp = async (data: SignUpData): Promise<SignUpResult> => {
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email: data.email,
      password: data.password,
      options: {
        emailRedirectTo: typeof window !== 'undefined' ? getOAuthCallbackUrl() : undefined,
        data: {
          first_name: data.firstName,
          last_name: data.lastName,
          phone: data.phone,
          role: data.role,
          client_dashboard_mode: data.role === 'client' ? 'buyer' : undefined,
          organization_name: data.organizationName || null,
          license_number: data.licenseNumber || null,
          terms_accepted: true,
        },
      },
    });

    if (authError) throw authError;

    // Profile will be created by the database trigger
    if (authData.user && !authData.session) {
      // Email confirmation required
      toast({
        title: 'Check your email',
        description: 'We sent you a verification link. Please check your email to confirm your account.',
      });

      return { requiresEmailConfirmation: true };
    }

    return { requiresEmailConfirmation: false };
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setProfile(null);
    setClientDashboardMode('buyer');
    setSentryUser(null);
  };

  const isSuperAdmin = useMemo(() => {
    return hasSuperAdminAccess(profile);
  }, [profile]);

  return (
    <AuthContext.Provider value={{ user, session, profile, clientDashboardMode, isLoading, isSuperAdmin, refreshProfile, updateClientDashboardMode, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
